'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

type DocsNavItem = {
  title: string;
  href?: string;
  method?: string;
  items?: DocsNavItem[];
};

// This will be replaced with actual data from the API later
const docsNavigation: DocsNavItem[] = [
  {
    title: 'Getting Started',
    href: '/docs',
    items: [],
  },
  {
    title: 'API Reference',
    items: [
      {
        title: 'Introduction',
        href: '/docs/api-reference',
      },
      {
        title: 'Authentication',
        href: '/docs/api-reference/authentication',
      },
      {
        title: 'Campaign Segments',
        href: '/docs/api-reference/campaign-segments',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/campaign-segments',
          },
          {
            title: 'Create Campaign Segment',
            href: '/docs/api-reference/campaign-segments/create',
            method: 'POST',
          },
          {
            title: 'Delete Campaign Segment',
            href: '/docs/api-reference/campaign-segments/delete',
            method: 'DELETE',
          },
          {
            title: 'Export Segment Contacts',
            href: '/docs/api-reference/campaign-segments/export-contacts',
            method: 'POST',
          },
          {
            title: 'Get Campaign Segment',
            href: '/docs/api-reference/campaign-segments/get',
            method: 'GET',
          },
          {
            title: 'JSON Query to SQL',
            href: '/docs/api-reference/campaign-segments/json-to-sql',
            method: 'POST',
          },
          {
            title: 'List Campaign Segments',
            href: '/docs/api-reference/campaign-segments/list',
            method: 'GET',
          },
          {
            title: 'Run Segment Query',
            href: '/docs/api-reference/campaign-segments/run-query',
            method: 'POST',
          },
          {
            title: 'Segment Schema Builder',
            href: '/docs/api-reference/campaign-segments/schema-builder',
            method: 'POST',
          },
          {
            title: 'Split Campaign Segment',
            href: '/docs/api-reference/campaign-segments/split',
            method: 'POST',
          },
          {
            title: 'Update Campaign Segment',
            href: '/docs/api-reference/campaign-segments/update',
            method: 'PATCH',
          },
        ],
      },
      {
        title: 'Activities',
        href: '/docs/api-reference/activities',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/activities',
          },
          {
            title: 'List Activities',
            href: '/docs/api-reference/activities/list',
            method: 'GET',
          },
          {
            title: 'Get Activity',
            href: '/docs/api-reference/activities/get',
            method: 'GET',
          },
          {
            title: 'Create Activity',
            href: '/docs/api-reference/activities/create',
            method: 'POST',
          },
          {
            title: 'Update Activity',
            href: '/docs/api-reference/activities/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Activity',
            href: '/docs/api-reference/activities/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Activity Types',
        href: '/docs/api-reference/activity-types',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/activity-types',
          },
          {
            title: 'List Activity Types',
            href: '/docs/api-reference/activity-types/list',
            method: 'GET',
          },
          {
            title: 'Get Activity Type',
            href: '/docs/api-reference/activity-types/get',
            method: 'GET',
          },
          {
            title: 'Create Activity Type',
            href: '/docs/api-reference/activity-types/create',
            method: 'POST',
          },
          {
            title: 'Update Activity Type',
            href: '/docs/api-reference/activity-types/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Activity Type',
            href: '/docs/api-reference/activity-types/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'AI Assistant Skills',
        href: '/docs/api-reference/ai-assistant-skills',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/ai-assistant-skills',
          },
          {
            title: 'List AI Assistant Skills',
            href: '/docs/api-reference/ai-assistant-skills/list',
            method: 'GET',
          },
          {
            title: 'Get AI Assistant Skill',
            href: '/docs/api-reference/ai-assistant-skills/get',
            method: 'GET',
          },
          {
            title: 'Create AI Assistant Skill',
            href: '/docs/api-reference/ai-assistant-skills/create',
            method: 'POST',
          },
          {
            title: 'Update AI Assistant Skill',
            href: '/docs/api-reference/ai-assistant-skills/update',
            method: 'PATCH',
          },
          {
            title: 'Delete AI Assistant Skill',
            href: '/docs/api-reference/ai-assistant-skills/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'AI Assistants',
        href: '/docs/api-reference/ai-assistants',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/ai-assistants',
          },
          {
            title: 'List AI Assistants',
            href: '/docs/api-reference/ai-assistants/list',
            method: 'GET',
          },
          {
            title: 'Get AI Assistant',
            href: '/docs/api-reference/ai-assistants/get',
            method: 'GET',
          },
          {
            title: 'Create AI Assistant',
            href: '/docs/api-reference/ai-assistants/create',
            method: 'POST',
          },
          {
            title: 'Update AI Assistant',
            href: '/docs/api-reference/ai-assistants/update',
            method: 'PATCH',
          },
          {
            title: 'Delete AI Assistant',
            href: '/docs/api-reference/ai-assistants/delete',
            method: 'DELETE',
          },
          {
            title: 'Assistant History',
            href: '/docs/api-reference/ai-assistants/history',
            method: 'GET',
          },
          {
            title: 'Restore Assistant',
            href: '/docs/api-reference/ai-assistants/restore',
            method: 'POST',
          },
          {
            title: 'Voice Configuration',
            href: '/docs/api-reference/ai-assistants/voice',
            method: 'PATCH',
          },
        ],
      },
      {
        title: 'AI Models',
        href: '/docs/api-reference/ai-models',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/ai-models',
          },
          {
            title: 'List AI Models',
            href: '/docs/api-reference/ai-models/list',
            method: 'GET',
          },
          {
            title: 'Get AI Model',
            href: '/docs/api-reference/ai-models/get',
            method: 'GET',
          },
        ],
      },
      {
        title: 'API Tools',
        href: '/docs/api-reference/api-tools',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/api-tools',
          },
          {
            title: 'List API Tools',
            href: '/docs/api-reference/api-tools/list',
            method: 'GET',
          },
          {
            title: 'Get API Tool',
            href: '/docs/api-reference/api-tools/get',
            method: 'GET',
          },
          {
            title: 'Create API Tool',
            href: '/docs/api-reference/api-tools/create',
            method: 'POST',
          },
          {
            title: 'Update API Tool',
            href: '/docs/api-reference/api-tools/update',
            method: 'PATCH',
          },
          {
            title: 'Delete API Tool',
            href: '/docs/api-reference/api-tools/delete',
            method: 'DELETE',
          },
          {
            title: 'Test API Tool',
            href: '/docs/api-reference/api-tools/test',
            method: 'POST',
          },
        ],
      },
      {
        title: 'Companies',
        href: '/docs/api-reference/companies',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/companies',
          },
          {
            title: 'List Companies',
            href: '/docs/api-reference/companies/list',
            method: 'GET',
          },
          {
            title: 'Get Company',
            href: '/docs/api-reference/companies/get',
            method: 'GET',
          },
          {
            title: 'Create Company',
            href: '/docs/api-reference/companies/create',
            method: 'POST',
          },
          {
            title: 'Update Company',
            href: '/docs/api-reference/companies/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Company',
            href: '/docs/api-reference/companies/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Contacts',
        href: '/docs/api-reference/contacts',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/contacts',
          },
          {
            title: 'List Contacts',
            href: '/docs/api-reference/contacts/list',
            method: 'GET',
          },
          {
            title: 'Get Contact',
            href: '/docs/api-reference/contacts/get',
            method: 'GET',
          },
          {
            title: 'Create Contact',
            href: '/docs/api-reference/contacts/create',
            method: 'POST',
          },
          {
            title: 'Update Contact',
            href: '/docs/api-reference/contacts/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Contact',
            href: '/docs/api-reference/contacts/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Conversations',
        href: '/docs/api-reference/conversations',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/conversations',
          },
          {
            title: 'Conversations Bulk Actions',
            href: '/docs/api-reference/conversations/bulk-actions',
            method: 'POST',
          },
          {
            title: 'Delete Conversation',
            href: '/docs/api-reference/conversations/delete',
            method: 'DELETE',
          },
          {
            title: 'Delete Conversation Message',
            href: '/docs/api-reference/conversations/messages-delete',
            method: 'DELETE',
          },
          {
            title: 'Get Conversation',
            href: '/docs/api-reference/conversations/get',
            method: 'GET',
          },
          {
            title: 'List Conversation Messages',
            href: '/docs/api-reference/conversations/messages-list',
            method: 'GET',
          },
          {
            title: 'List Conversations',
            href: '/docs/api-reference/conversations/list',
            method: 'GET',
          },
          {
            title: 'Message Composer AI',
            href: '/docs/api-reference/conversations/message-composer-ai',
            method: 'POST',
          },
          {
            title: 'Patch Conversation',
            href: '/docs/api-reference/conversations/patch',
            method: 'PATCH',
          },
          {
            title: 'Predefined Messages',
            href: '/docs/api-reference/conversations/predefined-messages',
            method: 'GET',
          },
          {
            title: 'Run Automation from Conversation',
            href: '/docs/api-reference/conversations/run-automation',
            method: 'POST',
          },
          {
            title: 'Send Outgoing Message',
            href: '/docs/api-reference/conversations/send-outgoing-message',
            method: 'POST',
          },
          {
            title: 'Upload Conversation Attachment',
            href: '/docs/api-reference/conversations/upload-attachment',
            method: 'POST',
          },
          {
            title: 'Start a WhatsApp Conversation',
            href: '/docs/api-reference/conversations/initiate-whatsapp',
          },
          {
            title: 'WhatsApp Templates in Conversations',
            href: '/docs/api-reference/conversations/whatsapp-templates',
          },
        ],
      },
      {
        title: 'Deals',
        href: '/docs/api-reference/deals',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/deals',
          },
          {
            title: 'List Deals',
            href: '/docs/api-reference/deals/list',
            method: 'GET',
          },
          {
            title: 'Get Deal',
            href: '/docs/api-reference/deals/get',
            method: 'GET',
          },
          {
            title: 'Create Deal',
            href: '/docs/api-reference/deals/create',
            method: 'POST',
          },
          {
            title: 'Update Deal',
            href: '/docs/api-reference/deals/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Deal',
            href: '/docs/api-reference/deals/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Deal Products',
        href: '/docs/api-reference/deal-products',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/deal-products',
          },
          {
            title: 'List Deal Products',
            href: '/docs/api-reference/deal-products/list',
            method: 'GET',
          },
          {
            title: 'Get Deal Product',
            href: '/docs/api-reference/deal-products/get',
            method: 'GET',
          },
          {
            title: 'Add Product to Deal',
            href: '/docs/api-reference/deal-products/create',
            method: 'POST',
          },
          {
            title: 'Update Deal Product',
            href: '/docs/api-reference/deal-products/update',
            method: 'PATCH',
          },
          {
            title: 'Remove Product from Deal',
            href: '/docs/api-reference/deal-products/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'MCP Tool Connections',
        href: '/docs/api-reference/mcp-tool-connections',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/mcp-tool-connections',
          },
          {
            title: 'Test MCP Connection',
            href: '/docs/api-reference/mcp-tool-connections/test',
            method: 'POST',
          },
        ],
      },
      {
        title: 'Organizations',
        href: '/docs/api-reference/organizations',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/organizations',
          },
          {
            title: 'List Organizations',
            href: '/docs/api-reference/organizations/list',
            method: 'GET',
          },
          {
            title: 'Get Organization',
            href: '/docs/api-reference/organizations/get',
            method: 'GET',
          },
          {
            title: 'Create Organization',
            href: '/docs/api-reference/organizations/create',
            method: 'POST',
          },
          {
            title: 'Update Organization',
            href: '/docs/api-reference/organizations/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Organization',
            href: '/docs/api-reference/organizations/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Organization Users',
        href: '/docs/api-reference/organizations-users-invites',
      },
      {
        title: 'Pipeline Stages',
        href: '/docs/api-reference/pipeline-stages',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/pipeline-stages',
          },
          {
            title: 'List Pipeline Stages',
            href: '/docs/api-reference/pipeline-stages/list',
            method: 'GET',
          },
          {
            title: 'Get Pipeline Stage',
            href: '/docs/api-reference/pipeline-stages/get',
            method: 'GET',
          },
          {
            title: 'Create Pipeline Stage',
            href: '/docs/api-reference/pipeline-stages/create',
            method: 'POST',
          },
          {
            title: 'Update Pipeline Stage',
            href: '/docs/api-reference/pipeline-stages/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Pipeline Stage',
            href: '/docs/api-reference/pipeline-stages/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Pipelines',
        href: '/docs/api-reference/pipelines',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/pipelines',
          },
          {
            title: 'List Pipelines',
            href: '/docs/api-reference/pipelines/list',
            method: 'GET',
          },
          {
            title: 'Get Pipeline',
            href: '/docs/api-reference/pipelines/get',
            method: 'GET',
          },
          {
            title: 'Create Pipeline',
            href: '/docs/api-reference/pipelines/create',
            method: 'POST',
          },
          {
            title: 'Update Pipeline',
            href: '/docs/api-reference/pipelines/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Pipeline',
            href: '/docs/api-reference/pipelines/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Products',
        href: '/docs/api-reference/products',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/products',
          },
          {
            title: 'List Products',
            href: '/docs/api-reference/products/list',
            method: 'GET',
          },
          {
            title: 'Get Product',
            href: '/docs/api-reference/products/get',
            method: 'GET',
          },
          {
            title: 'Create Product',
            href: '/docs/api-reference/products/create',
            method: 'POST',
          },
          {
            title: 'Update Product',
            href: '/docs/api-reference/products/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Product',
            href: '/docs/api-reference/products/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Product Categories',
        href: '/docs/api-reference/product-categories',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/product-categories',
          },
          {
            title: 'List Product Categories',
            href: '/docs/api-reference/product-categories/list',
            method: 'GET',
          },
          {
            title: 'Get Product Category',
            href: '/docs/api-reference/product-categories/get',
            method: 'GET',
          },
          {
            title: 'Create Product Category',
            href: '/docs/api-reference/product-categories/create',
            method: 'POST',
          },
          {
            title: 'Update Product Category',
            href: '/docs/api-reference/product-categories/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Product Category',
            href: '/docs/api-reference/product-categories/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Product Media',
        href: '/docs/api-reference/product-media',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/product-media',
          },
          {
            title: 'List Product Media',
            href: '/docs/api-reference/product-media/list',
            method: 'GET',
          },
          {
            title: 'Get Product Media',
            href: '/docs/api-reference/product-media/get',
            method: 'GET',
          },
          {
            title: 'Upload Product Media',
            href: '/docs/api-reference/product-media/create',
            method: 'POST',
          },
          {
            title: 'Update Product Media',
            href: '/docs/api-reference/product-media/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Product Media',
            href: '/docs/api-reference/product-media/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Reservation Available Slots',
        href: '/docs/api-reference/reservation-available-slots',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/reservation-available-slots',
          },
          {
            title: 'Get Reservation Available Slots',
            href: '/docs/api-reference/reservation-available-slots/list',
            method: 'GET',
          },
        ],
      },
      {
        title: 'Reservation Booking Links',
        href: '/docs/api-reference/reservation-booking-links',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/reservation-booking-links',
          },
          {
            title: 'List Reservation Booking Links',
            href: '/docs/api-reference/reservation-booking-links/list',
            method: 'GET',
          },
          {
            title: 'Get Reservation Booking Link',
            href: '/docs/api-reference/reservation-booking-links/get',
            method: 'GET',
          },
          {
            title: 'Create Reservation Booking Link',
            href: '/docs/api-reference/reservation-booking-links/create',
            method: 'POST',
          },
          {
            title: 'Update Reservation Booking Link',
            href: '/docs/api-reference/reservation-booking-links/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Reservation Booking Link',
            href: '/docs/api-reference/reservation-booking-links/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Reservation Resource Availability',
        href: '/docs/api-reference/reservation-resource-availability',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/reservation-resource-availability',
          },
          {
            title: 'List Reservation Resource Availability',
            href: '/docs/api-reference/reservation-resource-availability/list',
            method: 'GET',
          },
          {
            title: 'Create Reservation Resource Availability',
            href: '/docs/api-reference/reservation-resource-availability/create',
            method: 'POST',
          },
          {
            title: 'Delete Reservation Resource Availability',
            href: '/docs/api-reference/reservation-resource-availability/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Reservation Resource Exceptions',
        href: '/docs/api-reference/reservation-resource-exceptions',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/reservation-resource-exceptions',
          },
          {
            title: 'List Reservation Resource Exceptions',
            href: '/docs/api-reference/reservation-resource-exceptions/list',
            method: 'GET',
          },
          {
            title: 'Create Reservation Resource Exception',
            href: '/docs/api-reference/reservation-resource-exceptions/create',
            method: 'POST',
          },
          {
            title: 'Delete Reservation Resource Exception',
            href: '/docs/api-reference/reservation-resource-exceptions/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Reservation Resources',
        href: '/docs/api-reference/reservation-resources',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/reservation-resources',
          },
          {
            title: 'List Reservation Resources',
            href: '/docs/api-reference/reservation-resources/list',
            method: 'GET',
          },
          {
            title: 'Get Reservation Resource',
            href: '/docs/api-reference/reservation-resources/get',
            method: 'GET',
          },
          {
            title: 'Create Reservation Resource',
            href: '/docs/api-reference/reservation-resources/create',
            method: 'POST',
          },
          {
            title: 'Update Reservation Resource',
            href: '/docs/api-reference/reservation-resources/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Reservation Resource',
            href: '/docs/api-reference/reservation-resources/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Reservation Service Resources',
        href: '/docs/api-reference/reservation-service-resources',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/reservation-service-resources',
          },
          {
            title: 'List Reservation Service Resources',
            href: '/docs/api-reference/reservation-service-resources/list',
            method: 'GET',
          },
          {
            title: 'Create Reservation Service Resource',
            href: '/docs/api-reference/reservation-service-resources/create',
            method: 'POST',
          },
          {
            title: 'Update Reservation Service Resource',
            href: '/docs/api-reference/reservation-service-resources/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Reservation Service Resource',
            href: '/docs/api-reference/reservation-service-resources/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Reservation Services',
        href: '/docs/api-reference/reservation-services',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/reservation-services',
          },
          {
            title: 'List Reservation Services',
            href: '/docs/api-reference/reservation-services/list',
            method: 'GET',
          },
          {
            title: 'Get Reservation Service',
            href: '/docs/api-reference/reservation-services/get',
            method: 'GET',
          },
          {
            title: 'Create Reservation Service',
            href: '/docs/api-reference/reservation-services/create',
            method: 'POST',
          },
          {
            title: 'Update Reservation Service',
            href: '/docs/api-reference/reservation-services/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Reservation Service',
            href: '/docs/api-reference/reservation-services/delete',
            method: 'DELETE',
          },
        ],
      },
      {
        title: 'Reservations',
        href: '/docs/api-reference/reservations',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/reservations',
          },
          {
            title: 'List Reservations',
            href: '/docs/api-reference/reservations/list',
            method: 'GET',
          },
          {
            title: 'Get Reservation',
            href: '/docs/api-reference/reservations/get',
            method: 'GET',
          },
          {
            title: 'Create Reservation',
            href: '/docs/api-reference/reservations/create',
            method: 'POST',
          },
          {
            title: 'Update Reservation',
            href: '/docs/api-reference/reservations/update',
            method: 'PATCH',
          },
        ],
      },
      {
        title: 'Support Tickets',
        href: '/docs/api-reference/support-tickets',
        items: [
          {
            title: 'Overview',
            href: '/docs/api-reference/support-tickets',
          },
          {
            title: 'List Support Tickets',
            href: '/docs/api-reference/support-tickets/list',
            method: 'GET',
          },
          {
            title: 'Get Support Ticket',
            href: '/docs/api-reference/support-tickets/get',
            method: 'GET',
          },
          {
            title: 'Create Support Ticket',
            href: '/docs/api-reference/support-tickets/create',
            method: 'POST',
          },
          {
            title: 'Update Support Ticket',
            href: '/docs/api-reference/support-tickets/update',
            method: 'PATCH',
          },
          {
            title: 'Delete Support Ticket',
            href: '/docs/api-reference/support-tickets/delete',
            method: 'DELETE',
          },
        ],
      },
    ],
  },
  {
    title: 'Error Codes',
    items: [
      {
        title: 'Overview',
        href: '/docs/error-codes',
      },
      {
        title: 'WhatsApp',
        href: '/docs/error-codes/whatsapp',
        items: [
          {
            title: 'Overview',
            href: '/docs/error-codes/whatsapp',
          },
          {
            title: '130403 – Business Blocked',
            href: '/docs/error-codes/whatsapp/130403',
          },
          {
            title: '130472 – Marketing Experiment',
            href: '/docs/error-codes/whatsapp/130472',
          },
          {
            title: '130497 – Country Restriction',
            href: '/docs/error-codes/whatsapp/130497',
          },
          {
            title: '131000 – Something Went Wrong',
            href: '/docs/error-codes/whatsapp/131000',
          },
          {
            title: '131026 – Undeliverable',
            href: '/docs/error-codes/whatsapp/131026',
          },
          {
            title: '131031 – Account Locked',
            href: '/docs/error-codes/whatsapp/131031',
          },
          {
            title: '131042 – Payment Issue',
            href: '/docs/error-codes/whatsapp/131042',
          },
          {
            title: '131047 – Re-engagement',
            href: '/docs/error-codes/whatsapp/131047',
          },
          {
            title: '131049 – Ecosystem Limit',
            href: '/docs/error-codes/whatsapp/131049',
          },
          {
            title: '131050 – Opted Out',
            href: '/docs/error-codes/whatsapp/131050',
          },
          {
            title: '131051 – Unsupported Type',
            href: '/docs/error-codes/whatsapp/131051',
          },
          {
            title: '131052 – Media Download',
            href: '/docs/error-codes/whatsapp/131052',
          },
          {
            title: '131053 – Media Upload',
            href: '/docs/error-codes/whatsapp/131053',
          },
          {
            title: '131060 – Unavailable',
            href: '/docs/error-codes/whatsapp/131060',
          },
        ],
      },
    ],
  },
  {
    title: 'Tutorials',
    items: [
      {
        title: 'Quick Start',
        href: '/docs/tutorials/quick-start',
      },
      {
        title: 'Channels',
        items: [
          {
            title: 'Introduction',
            href: '/docs/tutorials/channels',
          },
          {
            title: 'WhatsApp',
            href: '/docs/tutorials/channels/whatsapp',
          },
          {
            title: 'Instagram',
            href: '/docs/tutorials/channels/instagram',
          },
          {
            title: 'Facebook',
            href: '/docs/tutorials/channels/facebook',
          },
        ],
      },
      {
        title: 'Contacts',
        items: [
          {
            title: 'Introduction',
            href: '/docs/tutorials/contacts',
          },
          {
            title: 'Contact Management',
            href: '/docs/tutorials/contacts/management',
          },
          {
            title: 'Import & Export',
            href: '/docs/tutorials/contacts/import-export',
          },
          {
            title: 'Contact Segments',
            href: '/docs/tutorials/contacts/segments',
          },
        ],
      },
      {
        title: 'Deals',
        items: [
          {
            title: 'Introduction',
            href: '/docs/tutorials/deals/introduction',
          },
          {
            title: 'Pipelines & Stages',
            href: '/docs/tutorials/deals/pipelines-stages',
          },
          {
            title: 'Amount & Products',
            href: '/docs/tutorials/deals/amount-products',
          },
        ],
      },
      {
        title: 'Products',
        items: [
          {
            title: 'Introduction',
            href: '/docs/tutorials/products/introduction',
          },
          {
            title: 'Catalog Management',
            href: '/docs/tutorials/products/catalog-management',
          },
          {
            title: 'Media & Variants',
            href: '/docs/tutorials/products/media-variants',
          },
        ],
      },
      {
        title: 'Tickets',
        items: [
          {
            title: 'Introduction',
            href: '/docs/tutorials/tickets/introduction',
          },
          {
            title: 'Ticket Management',
            href: '/docs/tutorials/tickets/management',
          },
          {
            title: 'Channels Integration',
            href: '/docs/tutorials/tickets/channels-integration',
          },
        ],
      },
    ],
  },
];

type DocsSidebarProps = {
  onNavigate?: () => void;
};

export default function DocsSidebar({ onNavigate }: DocsSidebarProps) {
  const pathname = usePathname();

  const renderNavItems = (items: DocsNavItem[]) => {
    return items.map((item, itemIndex) => {
      if (item.items && item.items.length > 0) {
        const isSubItemActive = item.items.some((subItem) =>
          subItem.href === pathname ||
          (subItem.items?.some((nestedSubItem) => nestedSubItem.href === pathname) ?? false)
        );
        
        return (
          <Collapsible
            key={itemIndex}
            defaultOpen={isSubItemActive}
            className="w-full"
          >
            <CollapsibleTrigger className={cn(
              "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium",
              isSubItemActive 
                ? "bg-gray-100 text-gray-900" 
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            )}>
              {item.title}
              <ChevronDown className="h-4 w-4" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-4">
              {renderNavItems(item.items)}
            </CollapsibleContent>
          </Collapsible>
        );
      }
      
      // Otherwise, render a simple link
      const isActive = item.href != null && pathname === item.href;
      const methodColors: Record<string, string> = {
        GET: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        PATCH: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        PUT: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        DELETE: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      };

      return (
        <Link
          key={itemIndex}
          href={item.href}
          onClick={() => onNavigate?.()}
          className={cn(
            "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm",
            isActive
              ? "bg-[#3983f7] text-white font-semibold"
              : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          )}
        >
          <span>{item.title}</span>
          {item.method && (
            <Badge
              variant="outline"
              className={cn(
                "ml-2 text-xs font-mono",
                isActive
                  ? "border-white/30 text-white/90"
                  : methodColors[item.method] || "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
              )}
            >
              {item.method}
            </Badge>
          )}
        </Link>
      );
    });
  };

  return (
    <div className="flex flex-col gap-2 px-4">
      {docsNavigation.map((section, index) => {
        // If the section has no subitems, render as a simple link
        if (section.items.length === 0 && section.href) {
          return (
            <Link
              key={index}
              href={section.href}
              onClick={() => onNavigate?.()}
              className={cn(
                "flex w-full items-center rounded-md px-3 py-2 text-sm font-medium",
                pathname === section.href
                  ? "bg-[#3983f7] text-white font-semibold"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              )}
            >
              {section.title}
            </Link>
          );
        }

        // Check if any of the subitems are active
        const isSubItemActive = section.items.some((item) => {
          if (item.href === pathname) return true;
          if (item.items) return item.items.some((subItem) => subItem.href === pathname);
          return false;
        });
        
        // For sections with subitems, render as a collapsible
        return (
          <Collapsible
            key={index}
            defaultOpen={isSubItemActive}
            className="w-full"
          >
            <CollapsibleTrigger className={cn(
              "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium",
              isSubItemActive 
                ? "bg-gray-100 text-gray-900" 
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            )}>
              {section.title}
              <ChevronDown className="h-4 w-4" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-4">
              {renderNavItems(section.items)}
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
} 